import {
  arrayToObj,
  getPercentageValue,
  sortMapAuxiliaryFunc,
  sortTimeListFunc,
} from '@/pages/opinion/utils';
import { dealwithPcConsoleTimeline } from '@op/pages/gamestore/utils';
import { DBO_TM, DBO_V } from '@op/utils/format';
import { getRadix } from '@op/utils/value';
import positiveIcon from '@op/assets/svg/steam_positive.svg';
import negativeIcon from '@op/assets/svg/steam_negative.svg';

export function dealwithTitle(text: string) {
  return text === '100+' ? '100h+' : `${text}h`;
}

export function sortLegend(key: string, reserved = true) {
  const sortMap = reserved
    ? {
        '0-5': 7,
        '5-10': 6,
        '10-20': 5,
        '20-50': 4,
        '50-80': 3,
        '80-100': 2,
        '100+': 1,
      }
    : {
        '0-5': 1,
        '5-10': 2,
        '10-20': 3,
        '20-50': 4,
        '50-80': 5,
        '80-100': 6,
        '100+': 7,
      };
  return sortMapAuxiliaryFunc(sortMap, item => item?.[key] || item.key);
}

/**
 * TODO: 其他系统使用
 * 处理成为timeline数据形式
 */
export function dealwithTimeline(
  reviews: Array<any> = [],
  oldReview: Array<any> = [],
  valueKey = 'value',
  options: { newZeroIsNull?: boolean; oldZeroIsNull?: boolean } = {
    newZeroIsNull: false,
    oldZeroIsNull: false,
  },
) {
  const { newZeroIsNull, oldZeroIsNull } = Object.assign(
    {
      newZeroIsNull: false,
      oldZeroIsNull: false,
    },
    options,
  );
  const oldYAxis: any[] = [];
  const yAxis: any[] = [];
  const xAxis: any[] = [];
  const timeMapValue: { [key: string]: string } = {};

  reviews.forEach((item: { [key: string]: any }, index: number) => {
    const oldItem = oldReview[index] || {};
    const newValue = item[valueKey] || 0;
    const oldValue = oldItem[valueKey] || 0;
    const time = item.time || '';
    xAxis.push(time);
    yAxis.push(newValue === 0 && newZeroIsNull ? null : newValue);
    oldYAxis.push(oldValue === 0 && oldZeroIsNull ? null : oldValue);

    timeMapValue[time] = oldItem.time || '';
  });

  return {
    oldYAxis,
    yAxis,
    xAxis,
    timeMapValue,
  };
}

/**
 * 处理为表格数据形式
 * @param reviews
 * @param oldReview
 * @param valueKey
 * @returns
 */
export function dealwithNewOldTimelineToTable(
  reviews: Array<any> = [],
  oldReview: Array<any> = [],
  valueKeys: Array<string> = ['value'],
) {
  const table: any[] = [];

  reviews.forEach((item: { [key: string]: any }, index: number) => {
    const oldItem = oldReview[index] || {};

    const valueMap = valueKeys.reduce((pre, key) => {
      const newValue = item[key] || 0;
      const oldValue = oldItem[key] || 0;
      return {
        ...pre,
        [`new_${key}`]: newValue,
        [`old_${key}`]: oldValue,
      };
    }, {});

    const time = item.time || '';

    table.push({
      time: time,
      old_time: oldItem.time || '',
      ...valueMap,
    });
  });

  return table;
}

type SelectedCommendType = Array<{
  key: string;
  review: number;
  score: number;
  time: string;
}>;
export function dealwithFilteredReviewsTableData(data: {
  select_non_recommend: SelectedCommendType;
  select_recommend: SelectedCommendType;
  select_recommend_score: SelectedCommendType;
}) {
  const { select_non_recommend, select_recommend, select_recommend_score } =
    data || {};
  select_recommend?.sort(sortTimeListFunc('time', 'desc'));
  const noRecommendMap = arrayToObj(select_non_recommend, 'time');
  const noRecommendScoreMap = arrayToObj(select_recommend_score, 'time');

  return (select_recommend || []).map(item => {
    const key = item.time;
    const noItem = noRecommendMap[key];
    const scoreItem = noRecommendScoreMap[key];

    return {
      time: item.time,
      select_non_recommend: noItem?.review || 0,
      select_recommend: item?.review || 0,
      select_recommend_score: (scoreItem?.score || 0) * 100,
    };
  });
}

export function dealwithFilteredReviewsNewOldTableData(data: {
  list: any;
  old_list: any;
}) {
  const newList = dealwithFilteredReviewsTableData(data?.list);
  const oldList = dealwithFilteredReviewsTableData(data?.old_list);

  return (newList || []).map((item, index) => {
    const oldValue = oldList[index];

    return {
      ...item,
      old_select_non_recommend: oldValue.select_non_recommend,
      old_select_recommend: oldValue.select_recommend,
      old_select_recommend_score: oldValue.select_recommend_score,
      old_time: oldValue.time,
    };
  });
}

export function dealwithLanguageTableData(timeline: Array<any>) {
  const list = Array.isArray(timeline) ? [...timeline] : [];

  list.sort(sortTimeListFunc('time', 'desc'));

  return list.map(item => {
    return {
      time: item.time,
      language: item.name,
      positive: item.positive_mentions || 0,
      negative: item.negative_mentions || 0,
      score:
        getPercentageValue(item.positive_mentions || 0, item.count || 0, 5) *
        100,
    };
  });
}

function getAllReviews(
  recommend: Array<any> = [],
  nonRecommend: Array<any> = [],
) {
  const totalRecommend = [];
  const length = Math.max(recommend.length, nonRecommend.length);

  for (let i = 0; i < length; i++) {
    const recommendItem = recommend[i];
    const nonRecommendItem = nonRecommend[i];
    const item = recommendItem || nonRecommendItem;
    totalRecommend.push({
      key: 'select_all_reviews',
      review: (recommendItem?.review || 0) + (nonRecommendItem?.review || 0),
      time: item.time,
    });
  }

  return totalRecommend;
}
export function dealwithSelectedTimeline(list: any, old_list: any) {
  const { select_recommend, select_non_recommend } = list || {};
  const {
    select_recommend: old_select_recommend,
    select_non_recommend: old_select_non_recommend,
  } = old_list || {};
  const newValue = getAllReviews(select_recommend, select_non_recommend);
  const oldValue = getAllReviews(
    old_select_recommend,
    old_select_non_recommend,
  );

  return {
    oldValue,
    newValue,
  };
}

export function stackedLineChart(
  newValue: Array<any>,
  options = { newZeroIsNull: false },
) {
  const list: any[][] = [];
  (newValue || []).map((_item, index) => {
    list.push(newValue.slice(0, index + 1));
  });
  const listInfo = list.map(items => {
    const sum = items.reduce((pre: number, cur: any) => {
      return pre + (cur.review || 0);
    }, 0);
    const info = items[items.length - 1];
    return {
      ...info,
      review: sum,
    };
  });
  if (options.newZeroIsNull) {
    let flag = false;
    for (let i = listInfo.length - 1; i >= 0; i--) {
      if (i > 0) {
        const newIndex = i;
        const oldIndex = i - 1;
        if (listInfo[newIndex].review === listInfo[oldIndex].review) {
          listInfo[newIndex] = { ...listInfo[newIndex], review: 0 };
        } else {
          flag = true;
        }
      }
      if (flag) break;
    }
  }

  return listInfo;
}

function geRatioThanValue(min: number, max: number) {
  const floorValue = Math.floor(min * 10) / 10; // 向下取整
  const ceilValue = Math.ceil(max * 10) / 10; // 向上取整
  return {
    min: floorValue,
    max: ceilValue,
  };
}

export function getYAxisMinMaxData(
  value: Array<any>,
  calculationMaxMin = true,
  isRatio = false,
) {
  if (!calculationMaxMin) return {};
  const realValue = value.filter(item => typeof item === 'number');
  let min = Math.min(...realValue);
  let max = Math.max(...realValue);

  if (isRatio) {
    return geRatioThanValue(min, max);
  }

  if (max / min > 4) {
    min = 0;
  } else if (min !== 0) {
    const radix = getRadix(min);
    const xMin = Math.floor(min * 0.95);
    min = Math.floor(xMin / radix) * radix;
  }

  if (max !== 0) {
    const radix = getRadix(max);
    const xMax = Math.ceil(max * 1.05);
    max = Math.ceil(xMax / radix) * radix;
  }

  return { min, max };
}

function dealwithTableData({
  newValue,
  oldValue,
  select_recommend,
  select_non_recommend,
  old_select_recommend,
  old_select_non_recommend,
}: any) {
  return (newValue || []).map((newAllItem: any, index: number) => {
    const oldAllItem = oldValue[index];
    const newGoodItem = select_recommend[index];
    const newBadItem = select_non_recommend[index];
    const oldGoodItem = old_select_recommend[index];
    const oldBadItem = old_select_non_recommend[index];

    return {
      time: newAllItem.time,
      old_time: oldAllItem.time,
      new_all_reviews: newAllItem.review,
      new_good_reviews: newGoodItem?.review || 0,
      new_bad_reviews: newBadItem?.review || 0,

      old_all_reviews: oldAllItem.review,
      old_good_reviews: oldGoodItem?.review || 0,
      old_bad_reviews: oldBadItem?.review || 0,
    };
  });
}

function dealwithCommonTimeline(list: any, old_list: any) {
  // all reviews
  const { newValue, oldValue } = dealwithSelectedTimeline(list, old_list);
  // new reviews
  const { select_recommend, select_non_recommend } = list || {};
  // old reviews
  const {
    select_recommend: old_select_recommend,
    select_non_recommend: old_select_non_recommend,
  } = old_list || {};

  return dealwithTableData({
    newValue,
    oldValue,
    select_recommend,
    select_non_recommend,
    old_select_recommend,
    old_select_non_recommend,
  });
}

function dealwithStackedTimeline(list: any, old_list: any) {
  // all reviews
  let { newValue, oldValue } = dealwithSelectedTimeline(list, old_list);
  newValue = stackedLineChart(newValue);
  oldValue = stackedLineChart(oldValue);
  // new reviews
  const { select_recommend: newGoodList, select_non_recommend: newBadList } =
    list;
  const select_recommend = stackedLineChart(newGoodList);
  const select_non_recommend = stackedLineChart(newBadList);
  // old reviews
  const { select_recommend: oldGoodList, select_non_recommend: oldBadList } =
    old_list;
  const old_select_recommend = stackedLineChart(oldGoodList);
  const old_select_non_recommend = stackedLineChart(oldBadList);

  return dealwithTableData({
    newValue,
    oldValue,
    select_recommend,
    select_non_recommend,
    old_select_recommend,
    old_select_non_recommend,
  });
}

export function downloadAddedReviewsDealwith(
  list: Array<any>,
  isStacked = false,
) {
  const globalResult = dealwithPcConsoleTimeline(list);
  const { list: new_list, old_list } = globalResult?.steam || {};

  if (!isStacked) {
    return dealwithCommonTimeline(new_list, old_list);
  }
  return dealwithStackedTimeline(new_list, old_list);
}

/**
 * 添加爬取容错，向前推holdNumber个时间端，无数据才显示0
 * @param item   图表数据
 * @param xAxis  时间轴
 * @param holdNumber 容错时间个数
 */
export function dealwithHoldData(
  item: { data: Array<number | null> },
  xAxis: Array<string>,
  holdNumber?: number,
) {
  try {
    const utcMinusTime = DBO_TM.utc().format('YYYY-MM-DD HH:mm:ss');
    item.data = item.data.map((item: number | null, index: number) => {
      const currentTime = xAxis[index];
      if (DBO_TM.isAfter(utcMinusTime, currentTime)) {
        return item;
      }
      return null;
    });
    // 如果是选中时间段的数据，那么就要做下面操作，对应”Realtime Ratings - Added Reviews“
    if (holdNumber) {
      // 如果最后值为0，那么就不展示该值，除非已经将当前时间段向前推两个值后还是没有数据
      let flagNum = holdNumber;
      for (let index = item.data.length - 1; index >= 0; index--) {
        const element = item.data[index];
        if (element === null || flagNum === 0) continue;
        if (element === 0) item.data[index] = null;
        if (element > 0) break;
        flagNum -= 1;
      }
    }
  } catch (error) {
    console.log(error);
  }

  return item;
}

export const columnsList = [
  {
    header: 'Duration',
    width: 105,
    key: 'key',
    align: 'left',
    format: dealwithTitle,
  },
  {
    header: 'Total Mentions',
    width: 150,
    key: 'count',
    format: (value: number) => DBO_V.reviews(value),
  },
  {
    header: 'Positive Mentions',
    width: 200,
    key: 'positive',
    icon: positiveIcon,
    format: (value: number) => DBO_V.reviews(value),
  },
  {
    header: 'Negative Mentions',
    width: 200,
    key: 'negative',
    icon: negativeIcon,
    format: (value: number) => DBO_V.reviews(value),
  },
  {
    header: 'Positive%',
    tableHeader: 'Positive%-2',
    width: 150,
    key: 'positive_ratio',
    downloadKey: 'positive_ratio_value',
    numFmt: ['0.00%'],
  },
  {
    header: 'Negative%',
    tableHeader: 'Negative%-2',
    width: 150,
    key: 'negative_ratio',
    downloadKey: 'negative_ratio_value',
    numFmt: ['0.00%'],
  },
];
